import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { countsBySubmitterSchema, type CountsBySubmitter } from '@/types/countsBySubmitter.types';
import { DEFAULT_PAGE_SIZE } from '../constants/leaveApplication.constants';
import {
  hierarchyFilterOptionListSchema,
  leaveApplicationDetailSchema,
  leaveApplicationSummarySchema,
  leaveUsageSummarySchema,
  paginatedLeaveApplicationsSchema,
  type HierarchyFilterOption,
  type HierarchyFilterType,
  type LeaveApplicationCountsBySubmitterParams,
  type LeaveApplicationDetail,
  type LeaveApplicationListParams,
  type LeaveApplicationSummary,
  type LeaveApplicationSummaryParams,
  type LeaveUsageSummary,
  type LeaveUsageSummaryParams,
  type PaginatedLeaveApplications,
  type ReviewLeaveApplicationPayload,
} from '../types/leaveApplication.types';

export const leaveApplicationQueryKeys = {
  all: () => ['leave-applications'] as const,
  lists: () => [...leaveApplicationQueryKeys.all(), 'list'] as const,
  listInfinite: (params: LeaveApplicationListParams) =>
    [...leaveApplicationQueryKeys.lists(), 'infinite', params] as const,
  summaries: () => [...leaveApplicationQueryKeys.all(), 'summary'] as const,
  summary: (params: LeaveApplicationSummaryParams) =>
    [...leaveApplicationQueryKeys.summaries(), params] as const,
  countsBySubmitter: (params: LeaveApplicationCountsBySubmitterParams) =>
    [...leaveApplicationQueryKeys.all(), 'counts-by-submitter', params] as const,
  hierarchyFilters: (type: HierarchyFilterType, amUserId: number | undefined) =>
    [...leaveApplicationQueryKeys.all(), 'hierarchy-filters', type, amUserId] as const,
  detail: (id: number) => [...leaveApplicationQueryKeys.all(), 'detail', id] as const,
  usageSummaries: () => [...leaveApplicationQueryKeys.all(), 'usage-summary'] as const,
  usageSummary: (id: number, params: LeaveUsageSummaryParams) =>
    [...leaveApplicationQueryKeys.usageSummaries(), id, params] as const,
};

export const leaveApplicationCountsBySubmitterQueryOptions = (
  params: LeaveApplicationCountsBySubmitterParams,
) =>
  queryOptions({
    queryKey: leaveApplicationQueryKeys.countsBySubmitter(params),
    queryFn: async (): Promise<CountsBySubmitter> => {
      const res = await apiClient.get(API.leaveApplication.countsBySubmitter, { params });
      return countsBySubmitterSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const leaveApplicationListInfiniteQueryOptions = (params: LeaveApplicationListParams) =>
  infiniteQueryOptions({
    queryKey: leaveApplicationQueryKeys.listInfinite(params),
    queryFn: async ({ pageParam }): Promise<PaginatedLeaveApplications> => {
      const res = await apiClient.get(API.leaveApplication.list, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_PAGE_SIZE },
      });
      return paginatedLeaveApplicationsSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const leaveApplicationSummaryQueryOptions = (params: LeaveApplicationSummaryParams) =>
  queryOptions({
    queryKey: leaveApplicationQueryKeys.summary(params),
    queryFn: async (): Promise<LeaveApplicationSummary> => {
      const res = await apiClient.get(API.leaveApplication.summary, { params });
      return leaveApplicationSummarySchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const leaveApplicationDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: leaveApplicationQueryKeys.detail(id),
    queryFn: async (): Promise<LeaveApplicationDetail> => {
      const res = await apiClient.get(API.leaveApplication.detail(id));
      return leaveApplicationDetailSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const leaveUsageSummaryQueryOptions = (id: number, params: LeaveUsageSummaryParams) =>
  queryOptions({
    queryKey: leaveApplicationQueryKeys.usageSummary(id, params),
    queryFn: async (): Promise<LeaveUsageSummary> => {
      const res = await apiClient.get(API.leaveApplication.usageSummary(id), { params });
      return leaveUsageSummarySchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function approveLeaveApplication(
  id: number,
  payload: ReviewLeaveApplicationPayload,
): Promise<void> {
  await apiClient.patch(API.leaveApplication.approve(id), payload);
}

export async function rejectLeaveApplication(
  id: number,
  payload: ReviewLeaveApplicationPayload,
): Promise<void> {
  await apiClient.patch(API.leaveApplication.reject(id), payload);
}

export const hierarchyFilterOptionsQueryOptions = (type: HierarchyFilterType, amUserId?: number) =>
  queryOptions({
    queryKey: leaveApplicationQueryKeys.hierarchyFilters(type, amUserId),
    queryFn: async (): Promise<HierarchyFilterOption[]> => {
      const res = await apiClient.get(API.leaveApplication.hierarchyFilters, {
        params: { type, amUserId },
      });
      return hierarchyFilterOptionListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });
